import "./cart-content";
import "./gift-note";

class MiniCart extends HTMLElement {
  // The drawer's backdrop is `pointer-events: none`, so an outside click never
  // reaches this element — it has to be caught on `document`. Attached only
  // while the drawer is open, and a frame late, so the click that opened it has
  // finished propagating to `document` before the handler exists.
  handleDocumentClick = (e) => {
    // Ignore clicks on items no longer in DOM
    if (!document.body.contains(e.target)) return;

    if (!this.content.contains(e.target)) {
      this._close();
    }
  };

  closeEsc = (e) => {
    if (e.key === "Escape") {
      this._close();
    }
  };

  handleOpen = () => this._open();
  handleClose = () => this._close();
  handleCloseButtonClick = () => this._close();

  connectedCallback() {
    this.closeButton = this.querySelector("[data-close]");
    this.content = this.querySelector(".mini-cart__content");

    this.closeButton?.addEventListener("click", this.handleCloseButtonClick);
    document.addEventListener("keydown", this.closeEsc);
    document.addEventListener("mini-cart:open", this.handleOpen);
    document.addEventListener("mini-cart:close", this.handleClose);

    // `/cart` redirects here with `?cart` — see section.main-cart.liquid.
    const url = new URL(window.location);
    if (!url.searchParams.has("cart")) return;

    url.searchParams.delete("cart");
    window.history.replaceState({}, "", url);
    this._open();
  }

  disconnectedCallback() {
    this.closeButton?.removeEventListener("click", this.handleCloseButtonClick);
    document.removeEventListener("keydown", this.closeEsc);
    document.removeEventListener("mini-cart:open", this.handleOpen);
    document.removeEventListener("mini-cart:close", this.handleClose);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  _open() {
    if (this.hasAttribute("open")) return;

    document.dispatchEvent(new CustomEvent("markup-modal:close"));

    this.style.display = "block";
    window.requestAnimationFrame(() => {
      const scroller = this.querySelector(".mini-cart__scroll-content");
      if (scroller) scroller.scrollTop = 0;

      this.setAttribute("open", "");
      window.openFullDimmer(this);
      window.lockScroll(this);
      this.returnFocusEl = document.activeElement;
      // Safari doesn't focus a button on click, so after a pointer open focus
      // arrives from <body> and Safari rings the close button as if by keyboard.
      // Only a keyboard open — its trigger still showing focus — lands there.
      const fromKeyboard = this.returnFocusEl?.matches(":focus-visible");
      window.trapFocus(this, fromKeyboard ? this.closeButton : this.content);
      document.addEventListener("click", this.handleDocumentClick);
    });
  }

  _close() {
    if (!this.hasAttribute("open")) return;

    this.removeAttribute("open");
    document.removeEventListener("click", this.handleDocumentClick);
    window.closeFullDimmer(this);

    const onEnd = () => {
      this.removeEventListener("transitionend", onEnd);

      if (this.hasAttribute("open")) return;
      this.style.display = null;
      window.removeTrapFocus(this.returnFocusEl);
      window.unlockScroll(this);
    };
    this.addEventListener("transitionend", onEnd);
  }
}

if (!customElements.get("mini-cart")) {
  customElements.define("mini-cart", MiniCart);
}
